import { RETURN_TO_PARAM } from "@arcadeai/ui-kit/utils/auth-redirect";
import { captureException, type Event, withScope } from "@sentry/react";
import { authClient } from "./auth-client";
import { LOGIN_REASON_PARAM, SESSION_EXPIRED_REASON } from "./const";
import { HttpError, isAuthRecoveryData } from "./http-error";
import { isStrongAuthChallenge, reportStrongAuthRequired } from "./strong-auth";

const AUTH_RECOVERY_HEADER = "x-bff-auth-recovery";

const DASHBOARD_CLIENT_HEADER = "Dashboard";

type QueryParamPrimitive = string | number | boolean | null | undefined;
type QueryParamValue = QueryParamPrimitive | QueryParamPrimitive[];

export type RequestQueryParams = Record<string, QueryParamValue>;

type ApiRequestConfig = {
  headers?: HeadersInit;
  params?: RequestQueryParams;
  signal?: AbortSignal;
};

type ApiResponse<T> = {
  data: T;
  status: number;
  headers: Headers;
};

type ApiRequestWithBody = ApiRequestConfig & {
  body?: unknown;
};

function applyQueryParams(url: URL, params?: RequestQueryParams) {
  if (!params) {
    return;
  }

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          url.searchParams.append(key, String(item));
        }
      }
      continue;
    }

    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  }
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof ReadableStream
  );
}

function resolveBody(body: unknown, headers: Headers): BodyInit | undefined {
  if (body === undefined) {
    return undefined;
  }

  if (isRawBody(body)) {
    return body;
  }

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return JSON.stringify(body);
}

function resolveUrl(
  baseURL: string,
  path: string,
  params?: RequestQueryParams
): URL {
  let url: URL;

  if (path === "") {
    url = new URL(baseURL);
  } else if (path.startsWith("http://") || path.startsWith("https://")) {
    url = new URL(path);
  } else {
    // Strip leading slash so new URL() resolves relative to the full baseURL
    // path, not the origin. "/workers" with baseURL ".../orgs/x/projects/y"
    // must resolve to ".../orgs/x/projects/y/workers", not ".../workers".
    const relative = path.startsWith("/") ? path.slice(1) : path;
    url = new URL(relative, baseURL.endsWith("/") ? baseURL : `${baseURL}/`);
  }

  applyQueryParams(url, params);
  return url;
}

async function parseResponseData(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();

  if (!text) {
    return undefined;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    try {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }

  return text;
}

async function isAuthRecoveryResponse(response: Response): Promise<boolean> {
  if (response.headers.get(AUTH_RECOVERY_HEADER)) {
    return true;
  }
  // Fall back to body inspection when the header isn't visible (CORS
  // misconfiguration, missing exposeHeaders, etc.) so genuine auth-recovery
  // 401s still trigger the OIDC redirect. Without this, the 401 would fall
  // through as a thrown HttpError and surface a raw error screen instead of
  // re-authenticating the user.
  if (response.status !== 401) {
    return false;
  }
  try {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const data = (await response.clone().json()) as unknown;
    return isAuthRecoveryData(data);
  } catch {
    return false;
  }
}

const REAUTH_ATTEMPT_KEY = "arcade.reauth_attempt";
const REAUTH_ATTEMPT_TTL_MS = 60_000;

// The recovery path kicks off a full-page navigation, so its request must
// never settle: an unsettled promise means no HttpError is constructed before
// the document unloads, and nothing reaches an error boundary.
const NEVER_RESOLVES: Promise<Response> = new Promise(() => {
  // Intentionally never resolves — see above.
});

let reauthInFlight = false;

// A bfcache restore revives the set guard in the cached heap and would swallow the next recovery 401.
if (
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function"
) {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      reauthInFlight = false;
    }
  });
}

type ReauthAttemptState =
  | { available: true; timestamp: number | null }
  | { available: false };

function readReauthAttempt(): ReauthAttemptState {
  try {
    const raw = sessionStorage.getItem(REAUTH_ATTEMPT_KEY);
    if (!raw) {
      return { available: true, timestamp: null };
    }
    const timestamp = Number(raw);
    return {
      available: true,
      timestamp: Number.isFinite(timestamp) ? timestamp : null,
    };
  } catch {
    return { available: false };
  }
}

function markReauthAttempt(): boolean {
  try {
    sessionStorage.setItem(REAUTH_ATTEMPT_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

// Sends the user to the interactive login page, tagged so it can explain why
// they landed there instead of staying on their page.
function interactiveLoginUrl(returnTo: string): string {
  const url = new URL("/login", window.location.origin);
  url.searchParams.set(RETURN_TO_PARAM, returnTo);
  url.searchParams.set(LOGIN_REASON_PARAM, SESSION_EXPIRED_REASON);
  return url.toString();
}

// Report only terminal recovery failures. Expected session expiry, storage
// restrictions, and a failed silent attempt that reaches interactive login are
// all recoverable and intentionally stay out of Sentry.
type AuthRecoveryFailurePhase =
  | "interactive-login-navigation"
  | "unexpected-navigation-failure";

type InteractiveLoginReason =
  | "authorize-navigation-rejected"
  | "authorize-request-failed"
  | "authorize-url-missing"
  | "recovery-loop-detected"
  | "storage-read-unavailable"
  | "storage-write-unavailable";

function safeErrorKind(error: unknown): string {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    switch (error.name) {
      case "AbortError":
        return "dom-abort-error";
      case "InvalidStateError":
        return "dom-invalid-state-error";
      case "NetworkError":
        return "dom-network-error";
      case "NotSupportedError":
        return "dom-not-supported-error";
      case "SecurityError":
        return "dom-security-error";
      case "SyntaxError":
        return "dom-syntax-error";
      default:
        return "dom-exception";
    }
  }
  if (error instanceof TypeError) {
    return "type-error";
  }
  if (error instanceof Error) {
    return "error";
  }
  return "non-error";
}

function minimalAuthRecoveryEvent(
  event: Event,
  phase: AuthRecoveryFailurePhase,
  errorKind: string,
  interactiveLoginReason?: InteractiveLoginReason
): Event {
  // This allowlist deliberately drops request/user data, breadcrumbs, custom
  // contexts, spans, extras, and global tags. The synthetic exception below
  // contains no caught error or URL, so the remaining payload cannot inherit
  // the return_to value or other page data from the Sentry SDK.
  return {
    dist: event.dist,
    environment: event.environment,
    event_id: event.event_id,
    exception: event.exception,
    level: event.level,
    platform: event.platform,
    release: event.release,
    tags: {
      authRecovery: phase,
      authRecoveryError: errorKind,
      ...(interactiveLoginReason
        ? { authRecoveryFallback: interactiveLoginReason }
        : {}),
    },
    timestamp: event.timestamp,
  };
}

function reportAuthRecoveryFailure(
  phase: AuthRecoveryFailurePhase,
  error: unknown,
  interactiveLoginReason?: InteractiveLoginReason
): void {
  const errorKind = safeErrorKind(error);
  withScope((scope) => {
    scope.setLevel("error");
    scope.addEventProcessor((event) =>
      minimalAuthRecoveryEvent(event, phase, errorKind, interactiveLoginReason)
    );
    captureException(new Error(`Auth recovery failed during ${phase}`));
  });
}

function navigateToInteractiveLogin(
  returnTo: string,
  reason: InteractiveLoginReason
): void {
  try {
    window.location.assign(interactiveLoginUrl(returnTo));
  } catch (error) {
    // Do not attach the rejected URL or browser error: the return_to query can
    // contain customer data. Only fixed-category diagnostics are reported.
    reauthInFlight = false;
    reportAuthRecoveryFailure("interactive-login-navigation", error, reason);
  }
}

// Resolves the silent OIDC authorize URL and navigates to it. If that lookup
// fails, navigates to the interactive login page instead — either way the user
// always ends up somewhere they can recover from.
async function navigateToReauth(returnTo: string): Promise<void> {
  let authorizeUrl: string | undefined;
  let interactiveLoginReason: InteractiveLoginReason = "authorize-url-missing";
  try {
    const { data } = await authClient.signIn.oauth2({
      providerId: "oidc",
      callbackURL: returnTo,
      disableRedirect: true,
    });
    authorizeUrl = data?.url || undefined;
  } catch {
    interactiveLoginReason = "authorize-request-failed";
    // Fall through to the interactive login route.
  }
  if (authorizeUrl) {
    try {
      window.location.assign(authorizeUrl);
      return;
    } catch {
      interactiveLoginReason = "authorize-navigation-rejected";
      // A malformed or browser-rejected authorize URL should not strand the
      // request on the never-resolving recovery promise below.
    }
  }
  navigateToInteractiveLogin(returnTo, interactiveLoginReason);
}

// Session-expiry 401s are recoverable: re-authenticate with a full-page OIDC
// redirect, which is silent (auto-consent) while the Ory SSO session is alive.
// A repeat recovery 401 within the guard window means the silent round-trip
// didn't restore the session, so fall back to the interactive login route
// instead of redirect-looping.
function reauthenticate(): Promise<Response> {
  if (!reauthInFlight) {
    reauthInFlight = true;
    const returnTo = window.location.href;
    const attempt = readReauthAttempt();
    const loopingBack =
      attempt.available &&
      attempt.timestamp !== null &&
      Date.now() - attempt.timestamp < REAUTH_ATTEMPT_TTL_MS;

    // The loop marker must survive a full-page OAuth round-trip. If storage is
    // unavailable (or becomes unavailable while writing), skip the silent
    // attempt: an in-memory marker would disappear during navigation and could
    // trap the user in a redirect loop.
    if (!attempt.available) {
      navigateToInteractiveLogin(returnTo, "storage-read-unavailable");
    } else if (loopingBack) {
      navigateToInteractiveLogin(returnTo, "recovery-loop-detected");
    } else if (markReauthAttempt()) {
      navigateToReauth(returnTo).catch((error: unknown) => {
        // All known failures fall back inside navigateToReauth, so reaching
        // this catch means an unexpected terminal failure blocked recovery.
        reauthInFlight = false;
        reportAuthRecoveryFailure("unexpected-navigation-failure", error);
      });
    } else {
      navigateToInteractiveLogin(returnTo, "storage-write-unavailable");
    }
  }
  return NEVER_RESOLVES;
}

/**
 * Sends a request and returns the raw `Response`.
 * Use this for streaming or integrations that need fetch-compatible behavior.
 */
export async function sendRequest(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      "Arcade-Client": DASHBOARD_CLIENT_HEADER,
      ...Object.fromEntries(new Headers(init?.headers)),
    },
  });
  if (response.ok) {
    // Keep the marker until its TTL expires. A successful public/session-only
    // request can race with a failed token-authenticated request, so a generic
    // 2xx does not prove that the OAuth recovery completed successfully.
    return response;
  }
  if (await isAuthRecoveryResponse(response)) {
    return reauthenticate();
  }
  // Arcade refused the sign-in because it didn't use strong authentication.
  // The caller still gets the refusal; the operator is told where to fix it.
  if (
    response.status === 401 &&
    isStrongAuthChallenge(response.headers.get("www-authenticate"))
  ) {
    reportStrongAuthRequired();
  }
  return response;
}

async function sendApiRequest<T>(
  baseURL: string,
  path: string,
  method: string,
  config?: ApiRequestWithBody
): Promise<ApiResponse<T>> {
  const headers = new Headers(config?.headers);
  const url = resolveUrl(baseURL, path, config?.params);
  const response = await sendRequest(url, {
    method,
    headers,
    signal: config?.signal,
    body: resolveBody(config?.body, headers),
  });
  const data = await parseResponseData(response);

  if (!response.ok) {
    throw new HttpError(response, data, { method, url });
  }

  return {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    data: data as T,
    status: response.status,
    headers: response.headers,
  };
}

export type ApiClient = {
  get: <T>(path: string, config?: ApiRequestConfig) => Promise<ApiResponse<T>>;
  post: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  put: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  patch: <T>(
    path: string,
    body?: unknown,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
  delete: <T>(
    path: string,
    config?: ApiRequestConfig
  ) => Promise<ApiResponse<T>>;
};

export function createApiClient(args: {
  baseURL: string;
  defaultParams?: RequestQueryParams;
}): ApiClient {
  const { baseURL, defaultParams } = args;

  const withDefaults = (config?: ApiRequestConfig): ApiRequestWithBody => ({
    ...config,
    params: {
      ...defaultParams,
      ...config?.params,
    },
  });

  return {
    get: <T>(path: string, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "GET", withDefaults(config)),
    post: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "POST", {
        ...withDefaults(config),
        body,
      }),
    put: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "PUT", {
        ...withDefaults(config),
        body,
      }),
    patch: <T>(path: string, body?: unknown, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "PATCH", {
        ...withDefaults(config),
        body,
      }),
    delete: <T>(path: string, config?: ApiRequestConfig) =>
      sendApiRequest<T>(baseURL, path, "DELETE", withDefaults(config)),
  };
}
